<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import IndividualBeneficiaryForm from '@/modules/investing/components/individuals/individual-beneficiary-form.vue'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useBeneficiaryStore } from '@/modules/investing/stores/beneficiary-store'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { VButton, VIcon, VTable } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'

const beneficiaryStore = useBeneficiaryStore()
const individualStore = useIndividualStore()

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const beneficiaries = computed(() => individualStore.listBeneficiaries)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }

const fetch = async () => {
  await individualStore.fetchBeneficiaries(individual_id, { slug })
}

const deleteBeneficiary = async (beneficiary_id: number) => {
  if (!window.confirm('Are you sure?')) return
  await beneficiaryStore.removeBeneficiary(beneficiary_id)
  await fetch()
}

const showForm = () => {
  beneficiaryFormModal.open()
}

const addBeneficiary = async (payload) => {
  await beneficiaryStore.addBeneficiary(payload)
  await fetch()
  beneficiaryFormModal.close()
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const isAdmin = computed(
  () => individual_id === authStore.current_user.investor_id?.toString() || authStore.is_site_or_group_admin,
)
const can_make_changes = computed(() => individual_id === authStore.current_user.investor_id?.toString())

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const beneficiaryFormModal = useModal()

onMounted(async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  await fetch()
  hideSkeleton()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="beneficiaries">
      <div class="mb-6 flex flex-row-reverse items-center gap-3">
        <VButton :click="showForm" size="md" variant="v-blue" v-if="can_make_changes">
          <div class="mr-1 flex items-center space-x-2">
            <div><VIcon name="Plus" /></div>
            <div>Add Beneficiary</div>
          </div>
        </VButton>
      </div>
      <VTable
        :columns="[
          {
            key: 'name',
            name: 'Name',
            sorted: true,
            type: 'string',
            fixed: true,
            is_visible: true,
          },
          {
            key: 'allocation_percentage',
            name: 'Allocation Percent',
            type: 'percent',
            aggregate: 'sum',
            is_visible: true,
          },
          {
            key: 'type',
            name: 'Type',
            type: 'string',
            is_visible: true,
          },
          {
            key: 'birthdate',
            name: 'Birthdate',
            type: 'date',
            is_visible: true,
          },
          {
            key: 'relationship',
            name: 'Relationship',
            type: 'string',
            is_visible: true,
          },
          {
            key: 'tax_id',
            name: 'SSN / Tax ID',
            type: 'string',
            is_visible: true,
          },
          {
            key: 'actions',
            name: '',
            type: 'actions',
            is_visible: true,
          },
        ]"
        :items="beneficiaries"
        :skeleton="skeleton"
        name="individual-beneficiaries"
        :slots="['actions']"
      >
        <template #actions="{ item }">
          <div class="flex space-x-1">
            <VButton :click="() => deleteBeneficiary(item.id)" size="xs" v-if="can_make_changes"> Delete </VButton>
          </div>
        </template>
      </VTable>
    </IndividualAccountLayout>
    <IndividualBeneficiaryForm :modalStore="beneficiaryFormModal" :onSubmit="addBeneficiary" v-if="can_make_changes" />
  </IndividualLayout>
</template>
